/* eslint-disable @typescript-eslint/no-unused-vars */
import { PropType, type Ref } from 'vue';
import { SelectListItemType } from '@app/enums/SelectListItemType';
import type ISelectItem from '@app/interfaces/dtos/ISelectItem';
import { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { MultiselectMode } from '@app/enums/MultiselectMode';
import type { ModalStateFooter } from '@app/interfaces/modalState.interface';

export type ItemEntry = {
	item: ISelectItem;
	subItems?: ISelectItem[];
};

export const multiselectEmitsDefinition = {
	'updateValues': (_value: string | number | (string | number)[] | null): boolean => true,
	'open': (): boolean => true,
	'close': (): boolean => true,
	'changeContinent': (): boolean => true,
	'searchChange': (_value: string): boolean => true
};

export const multiselectPropsDefinition = {
	modelValue: {
		type: [String, Number, Array] as PropType<string | number | (string | number)[]>,
		default: null
	},
	options: {
		type: Array as PropType<IMultiselectOption[] | ISelectItem[]>,
		required: true
	},
	mode: {
		type: String as PropType<MultiselectMode>,
		default: MultiselectMode.SINGLE
	},
	maxHeight: {
		type: String,
		default: '18rem'
	},
	placeholder: {
		type: String,
		default: 'Start typing to search'
	},
	alwaysShowPlaceholder: {
		type: Boolean,
		default: false
	},
	disabled: {
		type: Boolean,
		default: false
	},
	searchable: {
		type: Boolean,
		default: true
	},
	canClear: {
		type: Boolean,
		default: true
	},
	noOptionsText: {
		type: String,
		default: 'Start typing to search'
	},
	noResultsText: {
		type: String,
		default: 'No results found'
	},
	showOptionsOnSearchOnly: {
		type: Boolean,
		default: false
	},
	loading: {
		type: Boolean,
		default: false
	},
	sortSelectedToTop: {
		type: Boolean,
		default: false
	},
	description: {
		type: String,
		default: ''
	},
	warning: {
		type: String,
		default: ''
	},
	selectedContinentName: {
		type: String as PropType<string | null>,
		default: null
	},
	footer: {
		type: Object as PropType<ModalStateFooter>,
		default: undefined
	},
	startTypingText:{
		type: String,
		default: 'Start typing to search'
	}
};

export interface MultiselectProps {
	modelValue: string | number | (string | number)[] | null;
	options: IMultiselectOption[] | ISelectItem[];
	mode: MultiselectMode;
	maxHeight: string;
	placeholder: string;
	alwaysShowPlaceholder: boolean;
	disabled: boolean;
	searchable: boolean;
	canClear: boolean;
	noOptionsText: string;
	noResultsText: string;
	showOptionsOnSearchOnly: boolean;
	loading: boolean;
	sortSelectedToTop: boolean;
	description: string;
	warning: string;
	isLocationFilter: boolean;
	footer?: ModalStateFooter;
}

export interface UseMultiselectOptions {
	props: MultiselectProps;
	emit: {
		(e: 'update:modelValue' | 'updateValues', value: string | number | (string | number)[] | null): void;
		(e: 'open' | 'close'): void;
		(e: 'searchChange', value: string): void;
	};
}

export interface UseMultiselectReturn {
	multiselectValue: Ref<string | number | (string | number)[] | null>;
	isOpen: Ref<boolean>;
	formattedOptions: Ref<{
		value: string | number;
		label: string;
		icon?: string;
		count?: number;
		disabled: boolean;
	}[]>;
	sortedOptions: Ref<{
		value: string | number;
		label: string;
		icon?: string;
		count?: number;
		disabled: boolean;
	}[]>;
	selectItemType: Ref<SelectListItemType>;
	mapOptionToSelectItem: (option: IMultiselectOption, isSelected: boolean) => ISelectItem;
	onOpen: () => void;
	onClose: () => void;
	searchQuery: Ref<string>;
	showOptions: Ref<boolean>;
	visibleOptions: Ref<{
		value: string | number;
		label: string;
		icon?: string;
		count?: number;
		disabled: boolean;
	}[]>;
	handleSearchChange: (query: string) => void;
	filterKey: Ref<string>;
	hasSelection: Ref<boolean>;
}
